import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../utils/cn';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: boolean;
  animate?: boolean;
}

const Skeleton: React.FC<SkeletonProps> = ({
  className,
  width,
  height,
  rounded = true,
  animate = true
}) => {
  const style: React.CSSProperties = {
    width: width || '100%',
    height: height || '1rem'
  };

  return (
    <motion.div
      className={cn(
        'bg-gray-200 dark:bg-gray-700',
        rounded && 'rounded',
        className
      )}
      style={style}
      animate={animate ? {
        opacity: [0.5, 1, 0.5],
      } : {}}
      transition={animate ? {
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut'
      } : {}}
    />
  );
};

// Pre-built skeleton components
export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6', className)}>
    <div className="flex items-center space-x-4 mb-4">
      <Skeleton width={48} height={48} className="rounded-full" />
      <div className="flex-1">
        <Skeleton height={20} className="mb-2" />
        <Skeleton height={16} width="60%" />
      </div>
    </div>
    <Skeleton height={16} className="mb-2" />
    <Skeleton height={16} width="80%" />
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
    {/* Header */}
    <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-b border-gray-200 dark:border-gray-600">
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} height={16} width={i === 0 ? '30%' : '20%'} />
        ))}
      </div>
    </div>
    
    {/* Rows */}
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-6 py-4">
          <div className="flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} height={16} width={colIndex === 0 ? '40%' : '25%'} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonGrid: React.FC<{ 
  items?: number; 
  columns?: number;
  className?: string;
}> = ({ 
  items = 6, 
  columns = 3,
  className 
}) => (
  <div className={cn(
    'grid gap-6',
    columns === 1 && 'grid-cols-1',
    columns === 2 && 'grid-cols-1 md:grid-cols-2',
    columns === 3 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    columns === 4 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    className
  )}>
    {Array.from({ length: items }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

export const SkeletonText: React.FC<{ 
  lines?: number;
  className?: string;
}> = ({ 
  lines = 3,
  className 
}) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        height={16} 
        width={i === lines - 1 ? '60%' : '100%'} 
      />
    ))}
  </div>
);

export const SkeletonAvatar: React.FC<{ 
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ 
  size = 'md',
  className 
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  return (
    <Skeleton 
      className={cn('rounded-full', sizeClasses[size], className)} 
    />
  );
};

export const SkeletonButton: React.FC<{ 
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ 
  size = 'md',
  className 
}) => {
  const sizeClasses = {
    sm: 'h-8 w-20',
    md: 'h-10 w-24',
    lg: 'h-12 w-32'
  };

  return (
    <Skeleton 
      className={cn('rounded-lg', sizeClasses[size], className)} 
    />
  );
};

export const SkeletonInput: React.FC<{ 
  className?: string;
}> = ({ className }) => (
  <Skeleton 
    height={40} 
    className={cn('rounded-lg', className)} 
  />
);

export const SkeletonImage: React.FC<{ 
  aspectRatio?: 'square' | 'video' | 'wide';
  className?: string;
}> = ({ 
  aspectRatio = 'square',
  className 
}) => {
  const aspectClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    wide: 'aspect-[16/9]'
  };

  return (
    <Skeleton 
      className={cn('rounded-lg', aspectClasses[aspectRatio], className)} 
    />
  );
};

export default Skeleton;
